import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { MultiSelectDropdown } from "@admin/components/atoms/Input/MultiSelect";
import { Option } from "@admin/components/atoms/Input/Select";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  AddBulkShift,
  addBulkShifts,
  dateToISO,
  LocationUser,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addDays, set } from "date-fns";
import { useCallback,useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface MultiPersonnelShiftModalProps extends ModalProps {
  selectedDate?: Date;
  locationUsers: LocationUser[];
  companyId: string;
  locationId: string;
}

export function AddNewMultiPersonnelShiftModal({
  selectedDate,
  locationUsers = [],
  companyId,
  locationId,
  onClose,
  ...props
}: MultiPersonnelShiftModalProps) {
  const { t, i18n } = useTranslation();
  const [selectedUsers, setSelectedUsers] = useState<Option[]>([]);
  const [calculatedTime, setCalculatedTime] = useState(0);

  const shiftSchema = z.object({
    start: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, t("shift.Invalid time format")),
    end: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, t("shift.Invalid time format")),
  });

  type ShiftFormValues = z.infer<typeof shiftSchema>;

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: state,
  } = useForm<ShiftFormValues>({
    resolver: zodResolver(shiftSchema),
  });

  const queryClient = useQueryClient();

  const startValue = watch("start");
  const endValue = watch("end");

  const calculateTimeDiff = () => {
    if (startValue && endValue) {
      const startTime = new Date(
        `${selectedDate?.toDateString()} ${startValue}`,
      );
      const endTime = new Date(`${selectedDate?.toDateString()} ${endValue}`);

      if (endTime < startTime) {
        endTime.setDate(endTime.getDate() + 1);
      }

      const diff = (endTime.getTime() - startTime.getTime()) / 1000 / 60 / 60;
      setCalculatedTime(diff);
    }
  };

  const mutation = useMutation({
    mutationFn: addBulkShifts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      onClose?.();
      toast.success(t("shift.Shifts have been planned"));
      reset();
      setSelectedUsers([]);
      setCalculatedTime(0);
    },
    onError: (err) => {
      toast.error(t("shift.Unable to plan shifts"));
    },
  });

  const handleClose = useCallback(() => {
    onClose?.();
    reset();
    setSelectedUsers([]);
    setCalculatedTime(0);
  }, [onClose, reset]);

  const onSubmit = useCallback(
    (data: ShiftFormValues) => {
      const [startHours, startMinutes] = startValue.split(":").map(Number);
      const [endHours, endMinutes] = endValue.split(":").map(Number);

      const startTime = set(selectedDate || new Date(), {
        hours: startHours,
        minutes: startMinutes,
        seconds: 0,
        milliseconds: 0,
      });

      let endTime = set(selectedDate || new Date(), {
        hours: endHours,
        minutes: endMinutes,
        seconds: 0,
        milliseconds: 0,
      });

      if (endTime < startTime) {
        endTime = addDays(endTime, 1);
      }

      const payload: AddBulkShift = {
        companyId: companyId,
        companyLocationId: locationId,
        users: selectedUsers.map((u) => u.value as string),
        shiftDates: [
          {
            plannedStartTime: dateToISO(startTime),
            plannedEndTime: dateToISO(endTime),
          },
        ],
      };

      mutation.mutate(payload);
    },
    [
      startValue,
      endValue,
      selectedDate,
      companyId,
      locationId,
      selectedUsers,
      mutation,
    ],
  );

  return (
    <Modal
      {...props}
      onClose={handleClose}
      actionButtons={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            {t("common.Discard")}
          </Button>
          <Button onClick={handleSubmit(onSubmit)} loading={mutation.isPending}>
            {t("personnel.Save Personnel")}
          </Button>
        </>
      }
      title={t("shift.Plan New Shifts to", {
        date: selectedDate
          ? Intl.DateTimeFormat(i18n.language, {
              day: "2-digit", // 'dd' karşılığı
              month: "short", // 'MMM' karşılığı
            }).format(selectedDate)
          : "",
      })}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col">
          <Label title={t("common.Select Personnel")}>
            <MultiSelectDropdown
              options={locationUsers.map((lu) => ({
                value: lu.userId,
                label: lu.firstName + " " + lu.lastName,
              }))}
              selectedOptions={selectedUsers}
              onSelect={(value) => setSelectedUsers(value)}
              placeholder={t("common.Select Personnel")}
              variant="filled"
              name="personnels"
            />
          </Label>
        </div>
        <div className="flex flex-col">
          <Label title={t("shift.Shift Beginning and End")}>
            <div className="flex flex-row gap-2.5">
              <Input
                placeholder={t("shift.Start")}
                type="time"
                {...register("start")}
                onBlur={calculateTimeDiff}
              />
              <Input
                {...register("end")}
                type="time"
                name="end"
                placeholder={t("shift.End")}
                onBlur={calculateTimeDiff}
              />
            </div>
          </Label>
          <ErrorMessage
            message={`${state.errors.start?.message ?? ""} ${state.errors.end?.message ?? ""}`}
          />
        </div>

        <Label title={t("shift.Calculated Time")}>
          <Input disabled value={calculatedTime.toFixed(2)} />
        </Label>
      </form>
    </Modal>
  );
}
